import { clone } from "./projects";
import { uid, pins, sourceKey } from "../engine/registry";
import { canConnect } from "../engine/validation";
export function connect(project, from, to) {
  const error = canConnect(project, from, to);
  if (error) throw new Error(error);
  const p = clone(project);
  p.connections = p.connections.filter(
    (e) => !(e.to.component === to.component && e.to.pin === to.pin),
  );
  p.connections.push({ id: uid("wire"), from, to, width: 1 });
  return p;
}
export function removeSelection(project, ids, wireId) {
  const p = clone(project),
    removed = new Set(ids);
  p.components = p.components.filter((c) => !removed.has(c.id));
  p.connections = p.connections.filter(
    (e) =>
      e.id !== wireId &&
      !removed.has(e.from.component) &&
      !removed.has(e.to.component),
  );
  p.observables = p.observables.filter((o) => !removed.has(o.source.component));
  return p;
}
export function copySelection(project, ids) {
  const selected = new Set(ids);
  return clone({
    components: project.components.filter((c) => selected.has(c.id)),
    connections: project.connections.filter((e) =>
      selected.has(e.to.component),
    ),
    observables: project.observables.filter((o) =>
      selected.has(o.source.component),
    ),
  });
}
export function pasteSelection(project, selection) {
  const p = clone(project),
    mapping = new Map(selection.components.map((c) => [c.id, uid("c")])),
    names = new Set(p.components.map((c) => c.name));
  const remap = (s) =>
    s.component && mapping.has(s.component)
      ? { ...s, component: mapping.get(s.component) }
      : s;
  selection.components.forEach((c) => {
    let name = c.name + " copy",
      n = 2;
    while (names.has(name)) name = c.name + " copy " + n++;
    names.add(name);
    p.components.push({
      ...clone(c),
      id: mapping.get(c.id),
      name,
      x: c.x + 40,
      y: c.y + 40,
      config: { ...c.config, output: "" },
    });
  });
  selection.connections.forEach((e) => {
    const from = remap(e.from);
    if (from.component && !p.components.some((c) => c.id === from.component))
      return;
    if (from.input && !p.inputs.some((i) => i.id === from.input)) return;
    p.connections.push({ ...clone(e), id: uid("wire"), from, to: remap(e.to) });
  });
  selection.observables.forEach((o) => {
    const source = remap(o.source);
    p.observables.push({ id: sourceKey(source), source });
  });
  return { project: p, ids: [...mapping.values()] };
}
export function changeComponent(project, id, patch) {
  const p = clone(project),
    index = p.components.findIndex((c) => c.id === id);
  if (index < 0) return p;
  const old = p.components[index],
    next = { ...old, ...patch };
  p.components[index] = next;
  if (next.type !== old.type) {
    const before = pins(old).inputs,
      after = pins(next).inputs;
    p.connections = p.connections.map((e) => {
      if (e.to.component !== id) return e;
      const pin = before.find((n) => n.id === e.to.pin);
      if (!pin) return e;
      const candidates = after.filter((n) => n.control === pin.control),
        i = before
          .filter((n) => n.control === pin.control)
          .findIndex((n) => n.id === pin.id);
      return candidates[i]
        ? { ...e, to: { component: id, pin: candidates[i].id } }
        : e;
    });
  }
  const validIn = new Set(pins(next).inputs.map((p) => p.id)),
    validOut = new Set(pins(next).outputs.map((p) => p.id));
  p.connections = p.connections.filter(
    (e) =>
      (e.to.component !== id || validIn.has(e.to.pin)) &&
      (e.from.component !== id || validOut.has(e.from.pin)),
  );
  p.observables = p.observables.filter(
    (o) => o.source.component !== id || validOut.has(o.source.pin),
  );
  return p;
}
